import { useAuth } from "../../context/auth-context";
import { apiQueryKey } from "./queryKeys";
import { useApiQuery } from "./useApiQuery";
import type {
  AdminTransactionSummariesData,
  AdminTransactionSummariesEnvelope,
} from "./adminTransactionSummaries";
import type { OrderSubtypeFilter } from "./useAdminTransactionsDataQuery";

export type UseAdminTransactionSummariesParams = {
  /** ISO date `YYYY-MM-DD`; omitted from the request when undefined (all time). */
  startDate?: string;
  endDate?: string;
  /** Sent as `orderSubtypes`; omitted when "all". */
  orderSubtype?: OrderSubtypeFilter;
  /** Combined with the session check; set false to skip the request (e.g. invalid client-side range). */
  enabled?: boolean;
};

/**
 * GET api/admin/transactions/summaries?type=orders[&orderSubtypes][&startDate][&endDate]
 *
 * When both dates are omitted, only `type=orders` is sent (unbounded range). If the
 * backend requires a date range, pass defaults from the page (e.g. 2024-01-01–2026-04-30).
 */
export function useAdminTransactionSummariesQuery(
  params: UseAdminTransactionSummariesParams = {}
) {
  const { isAuthenticated } = useAuth();
  const {
    startDate,
    endDate,
    orderSubtype,
    enabled: enabledParam = true,
  } = params;

  const orderSubtypes =
    orderSubtype && orderSubtype !== "all" ? orderSubtype : undefined;

  return useApiQuery<
    AdminTransactionSummariesEnvelope,
    AdminTransactionSummariesData
  >({
    queryKey: apiQueryKey("admin-transactions-summaries", {
      type: "orders",
      orderSubtypes: orderSubtypes ?? null,
      startDate: startDate ?? null,
      endDate: endDate ?? null,
    }),
    url: "api/admin/transactions/summaries",
    params: {
      type: "orders",
      orderSubtypes,
      startDate,
      endDate,
    },
    enabled: Boolean(isAuthenticated) && enabledParam,
    select: (body) => body.data,
  });
}
